import { supabase, SUPABASE_URL } from '../supabaseClient';
import type { EvaluationPlanSheetImageScope } from '../types/evaluationPlanSheetCanvas';
import {
  SLIDE_IMAGE_STORAGE_LIMIT_BYTES,
  notifySlideImageStorageChanged,
  type SlideImageStorageUsage,
} from './slideImageStorageService';

const BUCKET = 'evaluation-sheet-images';
const CACHE_CONTROL = '31536000';

function genStorageFileName(mimeType: string): string {
  const ext = mimeType === 'image/jpeg' ? 'jpg' : mimeType === 'image/png' ? 'png' : 'webp';
  const id = globalThis.crypto?.randomUUID?.();
  if (id) return `${id}.${ext}`;
  return `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
}

export function buildEvaluationSheetImageStoragePath(params: {
  userId: string;
  scope: EvaluationPlanSheetImageScope;
  workspaceId?: string | null;
  fileName: string;
}): string {
  const { userId, scope, workspaceId, fileName } = params;
  if (scope === 'admin') {
    return `${userId}/admin/${fileName}`;
  }
  const ws = (workspaceId ?? 'default').trim() || 'default';
  return `${userId}/personal/${ws}/${fileName}`;
}

export function isEvaluationSheetImageStoragePublicUrl(publicUrl: string): boolean {
  if (!publicUrl.trim() || !SUPABASE_URL) return false;
  const prefix = `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/`;
  return publicUrl.trim().startsWith(prefix);
}

export function storagePathFromEvaluationSheetImagePublicUrl(
  publicUrl: string,
  userId: string
): string | null {
  if (!publicUrl.trim() || !SUPABASE_URL) return null;
  const prefix = `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/`;
  if (!publicUrl.startsWith(prefix)) return null;
  const path = decodeURIComponent(publicUrl.slice(prefix.length).split('?')[0] ?? '');
  if (!path.startsWith(`${userId}/`)) return null;
  return path;
}

export async function fetchEvaluationSheetImageStorageUsage(): Promise<SlideImageStorageUsage | null> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) return null;
  const { data, error } = await supabase.rpc('get_user_image_storage_usage');
  if (error) {
    const fallback = await supabase.rpc('get_slide_image_storage_usage');
    if (fallback.error) throw fallback.error;
    const row = Array.isArray(fallback.data) ? fallback.data[0] : fallback.data;
    return {
      bytesUsed: Number((row as { bytes_used?: number })?.bytes_used ?? 0),
      bytesLimit: Number((row as { bytes_limit?: number })?.bytes_limit ?? SLIDE_IMAGE_STORAGE_LIMIT_BYTES),
    };
  }
  const row = Array.isArray(data) ? data[0] : data;
  return {
    bytesUsed: Number((row as { bytes_used?: number })?.bytes_used ?? 0),
    bytesLimit: Number((row as { bytes_limit?: number })?.bytes_limit ?? SLIDE_IMAGE_STORAGE_LIMIT_BYTES),
  };
}

export async function uploadEvaluationSheetImage(params: {
  blob: Blob;
  mimeType: string;
  scope: EvaluationPlanSheetImageScope;
  workspaceId?: string | null;
}): Promise<{ publicUrl: string; storagePath: string }> {
  const { blob, mimeType, scope, workspaceId = null } = params;
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  const userId = userData.user?.id?.trim();
  if (userErr || !userId) throw new Error('로그인이 필요합니다.');

  const usage = await fetchEvaluationSheetImageStorageUsage();
  if (usage && usage.bytesUsed + blob.size > usage.bytesLimit) {
    throw new Error('QUOTA_EXCEEDED');
  }

  const fileName = genStorageFileName(mimeType);
  const storagePath = buildEvaluationSheetImageStoragePath({ userId, scope, workspaceId, fileName });

  const { error: uploadErr } = await supabase.storage.from(BUCKET).upload(storagePath, blob, {
    contentType: mimeType,
    cacheControl: CACHE_CONTROL,
    upsert: false,
  });
  if (uploadErr) throw uploadErr;

  try {
    const { error: regErr } = await supabase.rpc('register_evaluation_sheet_image_asset', {
      p_path: storagePath,
      p_bytes: blob.size,
      p_scope: scope,
      p_workspace_id: scope === 'personal' ? workspaceId : null,
    });
    if (regErr) {
      await supabase.storage.from(BUCKET).remove([storagePath]);
      if (String(regErr.message ?? '').includes('QUOTA_EXCEEDED')) {
        throw new Error('QUOTA_EXCEEDED');
      }
      throw regErr;
    }
  } catch (e) {
    await supabase.storage.from(BUCKET).remove([storagePath]);
    throw e;
  }

  const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  const publicUrl = urlData.publicUrl;
  if (!publicUrl) throw new Error('이미지 URL을 만들지 못했습니다.');

  notifySlideImageStorageChanged();
  return { publicUrl, storagePath };
}

export async function deleteEvaluationSheetImageByPublicUrl(publicUrl: string): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id?.trim();
  if (!userId || !publicUrl.trim()) return;
  const path = storagePathFromEvaluationSheetImagePublicUrl(publicUrl, userId);
  if (!path) return;
  await supabase.rpc('release_evaluation_sheet_image_asset', { p_path: path });
  await supabase.storage.from(BUCKET).remove([path]);
  notifySlideImageStorageChanged();
}

export type EvaluationSheetImageReleaseResult = 'released' | 'referenced' | 'skipped' | 'unsupported';

/**
 * 어떤 평가계획/적용본도 참조하지 않을 때만 이미지 자산을 해제(용량 차감)하고 storage 객체를 삭제합니다.
 * 서버 함수(release_evaluation_sheet_image_asset_if_unreferenced)가 아직 없으면 'unsupported' — 아무것도 지우지 않음.
 */
export async function releaseEvaluationSheetImageIfUnreferenced(
  publicUrl: string,
  userId: string
): Promise<EvaluationSheetImageReleaseResult> {
  const path = storagePathFromEvaluationSheetImagePublicUrl(publicUrl, userId);
  if (!path) return 'skipped';
  const { data, error } = await supabase.rpc('release_evaluation_sheet_image_asset_if_unreferenced', {
    p_path: path,
  });
  if (error) {
    const msg = `${error.code ?? ''} ${error.message ?? ''}`;
    if (/PGRST202|42883|Could not find the function|does not exist/i.test(msg)) return 'unsupported';
    console.warn('평가지 이미지 해제 실패:', error.message);
    return 'skipped';
  }
  if (data !== true) return 'referenced';
  await supabase.storage.from(BUCKET).remove([path]);
  return 'released';
}
